import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Poll an async loader on an interval, pausing while the browser tab is hidden.
 *
 * The admin app talks to the API over REST (no Firestore subscriptions anywhere), so inbound SMS
 * has to be pulled. Pausing on hidden tabs keeps a left-open Messaging page from calling the API
 * all day, and the immediate re-poll on becoming visible means the operator never sees stale state.
 *
 * @param {() => Promise<void>} load        the fetch to run; must be stable (useCallback)
 * @param {number} intervalMs               delay between polls while visible
 * @param {boolean} [enabled=true]          pause entirely when false
 */
export const usePolling = (load, intervalMs, enabled = true) => {
  // Held in a ref so changing the callback never restarts the timer mid-cycle.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) return undefined;

    let timerId = null;
    const stop = () => {
      if (timerId) clearInterval(timerId);
      timerId = null;
    };
    const start = () => {
      stop();
      timerId = setInterval(() => loadRef.current(), intervalMs);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        loadRef.current(); // catch up immediately, then resume the cadence
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, enabled]);
};

/**
 * Run an async task while tracking its pending state, ignoring results that arrive after the
 * component unmounts or after a newer run of the same task has started.
 *
 * @returns {{run:(task:() => Promise<any>, options?:{silent?:boolean}) => Promise<any>, loading:boolean}}
 */
export const useLatestRequest = () => {
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);   // newest run wins; older results are discarded
  const pendingRef = useRef(0);     // how many non-silent runs are still in flight
  const mountedRef = useRef(true);

  // Set on mount as well as cleared on unmount. React 18 StrictMode mounts, unmounts and remounts every
  // effect in development, so a cleanup-only version latches to false on the simulated unmount and never
  // recovers: every result is then discarded and setLoading(false) never runs, which reads as a spinner
  // that never stops. Production builds strip StrictMode, so this only ever broke dev.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const run = useCallback(async (task, { silent = false } = {}) => {
    const requestId = ++requestIdRef.current;
    if (!silent) {
      pendingRef.current += 1;
      setLoading(true);
    }
    try {
      const result = await task();
      // Drop the result if it lost the race or the component is gone.
      if (requestId !== requestIdRef.current || !mountedRef.current) return undefined;
      return result;
    } finally {
      // Counted rather than compared against requestId: a silent poll starting mid-flight bumps the id,
      // which would otherwise leave the spinner of a visible load running forever.
      if (!silent) {
        pendingRef.current -= 1;
        if (pendingRef.current === 0 && mountedRef.current) setLoading(false);
      }
    }
  }, []);

  // `run` is deliberately stable (empty deps). Depend on IT, not on this object, in any useCallback
  // that an effect lists: the object is new every render, and `loading` changes on every request, so
  // either one in a dependency array turns a load-on-change effect into an infinite render loop.
  return { run, loading };
};
